import React, { Fragment, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { parseCookies } from "nookies";
import { useScrollData } from "scroll-data-hook";
import classnames from "classnames";
import { useStoreState } from "pullstate";

import Custom404 from "src/pages/404";
import { apiPath } from "shared/helpers/apiPath";
import { applicationRoutes } from "shared/helpers/applicationRouting";
import { UIStore } from "@/store/UIStore";
import { formatDate } from "@/helpers/formatDate";
import { ScrollDirection } from "@/helpers/pageHelpers/Store/interface";
import useRequest from "@/helpers/useRequest";
import { routePath } from "@/helpers/routePath";
import { apiClient } from "@/helpers/apiClient";
import {
  ArticleInterface,
  ArticleType,
} from "@/helpers/pageHelpers/Article/interface";
import ArrowIcon from "@/icons/arrow-long.svg";
import ShareIcon from "@/icons/share.svg";
import { Schema } from "@/components/MetaHead/Head.interface";
import { GridColumns } from "@/components/Container/Container.interface";
import Container from "@/components/Container";
import Typography from "@/components/Typography";
import WysiwygText from "@/components/WysiwygText";
import Slide from "@/components/Slide";
import Avatar from "@/components/Avatar";
import PageWrapper from "@/components/PageWrapper";
import IconButton from "@/components/IconButton";
import MetaHead from "@/components/MetaHead";
import PopularArticles from "@/components/PopularArticles";
import BookmarkButton from "@/components/BookmarkButton";
import TagGroup from "@/components/TagGroup";
import ShareModal from "@/components/Modals/ShareModal";
import { ContentTypeEnum } from "@/components/SubTagList/SubTagList.interface";
import ArticleTopAd from "@/components/Advertisement/ArticleTop";
import ArticleMobileTopAd from "@/components/Advertisement/ArticleMobileTop";
import ArticleSlideAd from "@/components/Advertisement/ArticleSlide";
import ArticleBottomAd from "@/components/Advertisement/ArticleBottom";
import { SourceEnum } from "@/components/PopularArticles/PopularArticles.interface";
import { ContentSourceEnum } from "@/components/BookmarkButton/BookmarkButton.interface";
import SkeletonLoader from "@/components/SkeletonLoader";
import Recommended from "@/components/Recommended";
import SidebarAd from "@/components/Advertisement/Sidebar";
import MatureArticleModal from "@/components/Modals/MatureArticleModal";
import { ContentAction } from "@/components/ContentAction";
import { getImgixURL } from "@/helpers/getImgixURL";
import { getDisplayableTags } from "@/helpers/getDisplayableTags";
import {
  CommentsType,
  TagInterface,
} from "@/helpers/pageHelpers/Common/interface";
import StripedLogo from "@/icons/newonce-stripes.svg";
import { VoteSection } from "@/components/VoteSection";
import { ContentType } from "@/components/VoteSection/VoteSection.interface";
import Comments from "@/components/Comments";
import { useDataLayer } from "@/helpers/useDataLayer";
import { getParsedJSON } from "@/helpers/getParsedJSON";

import styles from "./Article.module.scss";

const Related = dynamic(import("@/components/Related"), { ssr: false });

export const Article: React.FC<ArticleInterface> = ({
  initialData,
  related,
}) => {
  const router = useRouter();
  const { pushToDataLayer } = useDataLayer();
  const { from } = router.query;
  const slug = router.query?.slug || initialData?.slug;
  const request = useRequest(slug && apiPath.ARTICLES(slug), {
    initialData,
    revalidateOnMount: true,
  });
  const data = request.data || initialData;
  const { mutate, error } = request;

  const articleContent = useRef<HTMLDivElement>();
  const { data: articleSubtags } = useRequest(
    slug && apiPath.CONTENT_SUBTAGS(slug, ContentTypeEnum.Article),
    { initialData: initialData?.subtags || [], revalidateOnMount: true }
  );

  const [isShareModalOpen, openShareModal] = useState<boolean>(false);
  const [isMatureArticleModalOpen, setMatureArticleModal] = useState<boolean>(
    data?.matureContent
  );

  const { userLoggedIn } = useStoreState(UIStore, (store) => store);

  useEffect(() => {
    UIStore.update((store) => {
      store.articleScrollPositionY = 0;
    });
  }, []);

  useEffect(() => {
    setMatureArticleModal(data?.matureContent);
  }, [data?.matureContent]);

  const {
    direction: { y: scrollingDirection },
    position,
    relativeDistance,
  } = useScrollData({});
  // Article Read Indicator
  useEffect(() => {
    // Get Article Container Measures
    const articleBounds = articleContent?.current?.getBoundingClientRect();
    const articleHeight = articleBounds?.height;
    // Get Viewport Measures
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    // Set calculated viewport because of fixed menus differences
    const calculatedHeight =
      viewportWidth > 1024 ? viewportHeight - 88 : viewportHeight - 196;

    // Get Initial article height without Ads
    UIStore.update((store) => {
      store.articleLength = parseInt(
        (articleHeight - calculatedHeight).toFixed()
      );
    });

    // Update Read Article Progress
    if (isScrollingDown) {
      // Get article height with Ads after initial render
      UIStore.update((store) => {
        store.articleLength = parseInt(
          (articleHeight - calculatedHeight).toFixed()
        );
        store.articleScrollDirection = ScrollDirection.Down;
        store.articleScrollPositionY = position.y;
      });
    }

    if (isScrollingUp) {
      UIStore.update((store) => {
        store.articleScrollDirection = ScrollDirection.Up;
        store.articleScrollPositionY = position.y;
        store.articleScrollRelativeDistance = relativeScrollDistance;
      });
    }
  }, [articleContent, scrollingDirection]);

  useEffect(() => {
    UIStore.update((store) => {
      store.articleSlug = slug;
      store.articleBookmarked = data?.bookmarked;
    });
  }, [slug, data]);

  useEffect(() => {
    mutate();
  }, [userLoggedIn]);

  if (router.isFallback) {
    return (
      <PageWrapper>
        <Container grid={GridColumns.Three} className={styles.container}>
          <div className={styles.header}>
            <div className={styles.authors}>
              <Typography
                variant="small"
                component="p"
                className={styles.author}
              >
                <SkeletonLoader height={50} />
              </Typography>

              <Typography
                variant="small"
                component="time"
                className={styles.date}
              >
                <SkeletonLoader height={50} />
              </Typography>
            </div>

            <Typography component="h1" variant="h1" className={styles.title}>
              <SkeletonLoader height={50} />
            </Typography>
          </div>
        </Container>

        <Container grid={GridColumns.Three}>
          <article className={styles.body}>
            <figure className={styles.figure}>
              <SkeletonLoader height={400} />
            </figure>
            <div className={classnames(styles.content, styles.spaceLeft)}>
              <div className={styles.lead}>
                <SkeletonLoader height={250} />
              </div>

              <SkeletonLoader height={450} />
            </div>
          </article>
        </Container>
      </PageWrapper>
    );
  }

  if (!data || data?.error || !slug || error) {
    return <Custom404 />;
  }

  const lead = getParsedJSON(data.lead);
  const body = getParsedJSON(data.body);
  const displayableTags = getDisplayableTags(data.tags);
  const headerLabel = from ? from : "Artykuły";
  const isMiddleSlide = Math.floor(data?.gallery?.length / 2);
  const isScrollingDown =
    scrollingDirection === ScrollDirection.Down && position.y > 48;
  const isScrollingUp = scrollingDirection === ScrollDirection.Up;
  const relativeScrollDistance = relativeDistance.y;
  const cookies = parseCookies();
  const cookieExist = cookies?.ageConfirmation !== undefined;

  const pageData = {
    headerLabel: headerLabel as string,
    fallbackHistoryBack: {
      backUrl: routePath.FEED.href,
      as: routePath.FEED.href,
    },
  };

  const schemaMetaData = {
    type: Schema.Article,
    ...data.schemaMetaData,
    slug,
    tags: data.tags,
    subtags: [...articleSubtags, { title: ArticleType[data.articleType] }],
    articleType: ArticleType[data.articleType],
  };

  const metaData = {
    ...data.headMetaData,
    title: null,
    metaTitle: `${data.title} | newonce`,
  };

  const img = getImgixURL(data.mainPicture?.versions.original.url);

  const openModal = () => {
    openShareModal(true);
    pushToDataLayer("share");
  };

  const closeMatureArticleModal = () => {
    setMatureArticleModal(false);
  };

  return (
    <>
      <PageWrapper pageData={pageData} className={styles.pageWrapper}>
        <MetaHead metaData={metaData} schemaData={schemaMetaData} />

        <div ref={articleContent}>
          <ArticleTopAd />
          <Container
            grid={GridColumns.Three}
            className={classnames(
              styles.container,
              styles[process.env.NEXT_PUBLIC_PORTAL]
            )}
          >
            <StripedLogo className={styles.stripedLogo} />
            <div className={styles.header}>
              <div className={styles.buttonGroup}>
                <IconButton size="big" variant="primary" onClick={openModal}>
                  <ShareIcon />
                </IconButton>

                <BookmarkButton
                  size="big"
                  slug={slug}
                  source={ContentSourceEnum.Articles}
                  bookmarked={data.bookmarked}
                />
              </div>

              <div className={styles.authors}>
                <div className={styles.avatars}>
                  {data?.authors?.map((author) => (
                    <Avatar
                      key={author.id}
                      src={author.avatar.url}
                      alt={author.name}
                      className={styles.avatar}
                    />
                  ))}
                </div>

                {!!data?.authors?.length ? (
                  data.authors.map((author) => (
                    <Link
                      key={author.id}
                      href={{
                        pathname: `${applicationRoutes.AUTHOR}[slug]`,
                        query: { from: "Artykuł" },
                      }}
                      as={applicationRoutes.AUTHOR + author.slug}
                    >
                      <a className={styles.author}>
                        <Typography variant="small" component="p">
                          {author.name}
                        </Typography>
                      </a>
                    </Link>
                  ))
                ) : (
                  <Typography
                    variant="small"
                    component="p"
                    className={styles.author}
                  >
                    Redakcja
                  </Typography>
                )}

                <Typography
                  variant="small"
                  component="time"
                  className={styles.date}
                >
                  {formatDate(data.publishedAt)}
                </Typography>
              </div>

              <Typography component="h1" variant="h1" className={styles.title}>
                {data?.title}
              </Typography>
            </div>

            {data.relatedArticle && (
              <div className={styles.relatedArticle}>
                <ArrowIcon className={styles.icon} />

                <div className={styles.wrapper}>
                  <Typography variant="small">Zobacz również:</Typography>

                  <Link
                    href={`${applicationRoutes.ARTICLE}[slug]`}
                    as={applicationRoutes.ARTICLE + data.relatedArticle.slug}
                  >
                    <a className={styles.link}>
                      <Typography variant="h4">
                        {data.relatedArticle.title}
                      </Typography>
                    </a>
                  </Link>
                </div>
              </div>
            )}
          </Container>

          <Container grid={GridColumns.Three}>
            <article className={styles.body}>
              <figure className={styles.figure}>
                <div className={styles.image}>
                  <Image
                    src={img}
                    alt={data.mainPicture?.alt}
                    layout="fill"
                    objectFit="cover"
                    placeholder="blur"
                    blurDataURL={`https://newonce.imgix.net/${img}?fm=blurhash`}
                    priority
                  />
                </div>

                <figcaption
                  className={classnames(styles.caption, styles.spaceLeft)}
                >
                  {displayableTags && (
                    <TagGroup
                      tags={displayableTags as TagInterface[]}
                      className={styles.tags}
                    />
                  )}
                  <span>{data.mainPicture?.caption}</span>
                </figcaption>
              </figure>

              <ArticleMobileTopAd />

              <div className={classnames(styles.content, styles.spaceLeft)}>
                <div className={styles.lead}>
                  <WysiwygText data={lead} entities={data.entities} />
                </div>

                <WysiwygText data={body} entities={data.entities} isArticle />
                <div className={styles.gallery}>
                  {data?.gallery?.map((slide, index) => (
                    <Fragment key={index}>
                      <Slide
                        key={slide.id}
                        index={slide?.index ? slide.index : index + 1}
                        id={slide.id}
                        title={slide.title}
                        image={slide.image}
                        videoUrl={slide.videoUrl}
                        body={slide.body}
                        entities={data.entities}
                      />
                      {index === isMiddleSlide && <ArticleSlideAd />}
                    </Fragment>
                  ))}
                </div>
              </div>
            </article>

            <aside className={styles.popular}>
              <PopularArticles slug={slug} />
              <SidebarAd type={SourceEnum.Article} />
            </aside>
          </Container>

          <Container>
            <ArticleBottomAd />
          </Container>
        </div>

        <VoteSection
          source={ContentSourceEnum.Articles}
          data={data}
          subtags={articleSubtags}
          type={ContentType.ARTICLE}
        />

        <ContentAction
          onClick={openModal}
          slug={slug}
          bookmarked={data.bookmarked}
          authors={data.authors}
          tags={articleSubtags}
          source={ContentSourceEnum.Articles}
        />

        {data.showComments && (
          <Comments
            contentData={data}
            subtags={articleSubtags}
            slug={slug}
            type={CommentsType.Article}
          />
        )}

        <Related slug={slug} data={related} />

        <Recommended />
      </PageWrapper>

      <ShareModal
        onClose={() => openShareModal(false)}
        isOpen={isShareModalOpen}
        title={data.title}
      />

      {!cookieExist && (
        <MatureArticleModal
          onClose={closeMatureArticleModal}
          isOpen={isMatureArticleModalOpen}
        />
      )}
    </>
  );
};

export async function getStaticPaths() {
  const client = apiClient();
  const {
    data: { items },
  } = await client.get(apiPath.SLUGS({ perPage: 50, resource: "articles" }));
  const paths =
    items &&
    items.map((article) => ({
      params: { ...article },
    }));

  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps(context) {
  const { params } = context;
  const client = apiClient();

  try {
    const { data } = await client.get(apiPath.ARTICLES(params.slug));

    return {
      props: {
        initialData: {
          ...data,
          subtags: [],
        },
        related: [],
      },
      revalidate: 86400,
    };
  } catch (e) {
    return {
      props: {
        initialData: {
          error: true,
          slug: params.slug,
        },
      },
      revalidate: 43200,
    };
  }
}

export default Article;
